import React, { useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';

/**
 * Shared numeric input component for Division 8 forms.
 * - Blocks letters and negative signs
 * - Allows decimal numbers (e.g., 2.5, 0.75)
 * - If allowFraction=true, also allows inch fractions (e.g., 1/4, 3/8)
 * - If positive=true, sets a native HTML5 custom validity message when value is 0
 *   or negative, so the browser shows its native validation balloon on submit.
 * - min=0 enforced
 */
export function NumericInput({ value, onChange, allowFraction = false, positive = false, className = '', ...props }) {
  const inputRef = useRef(null);

  // Keep native validity in sync with current value when positive=true
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (positive && value !== '' && value !== null && value !== undefined) {
      const n = parseFloat(value);
      if (!isNaN(n) && n <= 0) {
        el.setCustomValidity('Value must be greater than 0');
        return;
      }
    }
    el.setCustomValidity('');
  }, [value, positive]);

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrows, home, end
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowed.includes(e.key)) return;
    // Allow Ctrl/Cmd+A, C, V, X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
    // Allow period (decimal)
    if (e.key === '.') return;
    // Allow slash for fractions in inch fields
    if (allowFraction && e.key === '/') return;
    // Allow space for inch fractions like "36 1/2"
    if (allowFraction && e.key === ' ') return;
    // Allow digits 0-9
    if (/^[0-9]$/.test(e.key)) return;
    // Block everything else (letters, minus, special chars)
    e.preventDefault();
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(raw);
      return;
    }
    if (allowFraction) {
      // Allow digits, dots, slashes, spaces for fractional inch (e.g., "36 1/2", "1/4")
      const sanitized = raw.replace(/[^0-9./\s]/g, '');
      onChange(sanitized);
    } else {
      // Only digits and one decimal point
      const sanitized = raw.replace(/[^0-9.]/g, '');
      // Prevent multiple dots
      const parts = sanitized.split('.');
      const clean = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
      onChange(clean);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (allowFraction) {
      if (/[^0-9./\s]/.test(pasted)) e.preventDefault();
    } else {
      if (/[^0-9.]/.test(pasted)) e.preventDefault();
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      min={undefined}
      className={className}
    />
  );
}
